import type { ReactElement } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Fallback } from './fallback.jsx';
import type { Props } from './types.js';
import { depot } from '#~/depot/index.js';
import { INITIAL_BOOT_OBSERVATION } from '#~/depot/types.js';
import { getBootId } from '#~/logger/index.js';
import {
  bootFailureCopy,
  publishBootLifecycleFailure,
  retryCurrentDocumentBoot
} from '#~/routes/root/bootstrap-analytics.js';

/**
 * React error boundary. Capture settles pre-success failure here because
 * ErrorBoundary unmounts BootOverlay, so writing depot.boot.error cannot
 * show the overlay error screen.
 *
 * @see https://bit.ly/3UKpYrS
 */
export const Boundary = ({ children }: Props): ReactElement => {
  return (
    <ErrorBoundary
      FallbackComponent={Fallback}
      onError={(error) => {
        const alreadyReady = depot.getState().boot.observation?.outcome === 'ready';
        if (alreadyReady) {
          return;
        }
        const bootId = getBootId();
        publishBootLifecycleFailure({ cause: error });
        const copy = bootFailureCopy(error);
        depot.getState().produce((draft) => {
          if (draft.boot.observation?.outcome === 'ready') {
            return;
          }
          draft.boot.observation = {
            ...(draft.boot.observation ?? INITIAL_BOOT_OBSERVATION),
            outcome: 'failed',
            bootId
          };
          draft.boot.error = {
            message: copy.message,
            detail: copy.detail,
            severity: 'retryable',
            retryFn: () => retryCurrentDocumentBoot()
          };
        });
      }}
    >
      {children}
    </ErrorBoundary>
  );
};
